// 音频录制工具 - 录制麦克风音频并转换为 WAV 格式 (PCM 8kHz)

export interface AudioRecorderOptions {
  sampleRate?: number;
  targetSampleRate?: number; // 目标采样率（降采样后）
  channelCount?: number;
}

export class AudioRecorder {
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private stream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private options: Required<AudioRecorderOptions>;

  constructor(options: AudioRecorderOptions = {}) {
    this.options = {
      sampleRate: 16000,
      targetSampleRate: 8000, // 默认降采样到 8kHz
      channelCount: 1,
      ...options,
    };
  }

  // 请求麦克风权限并开始录制
  async start(): Promise<void> {
    try {
      // 获取麦克风权限
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: this.options.sampleRate,
          channelCount: this.options.channelCount,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      // 创建 MediaRecorder
      const mimeType = this.getSupportedMimeType();
      this.mediaRecorder = new MediaRecorder(this.stream, {
        mimeType,
      });

      this.audioChunks = [];

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.start(100); // 每100ms收集一次数据
    } catch (err) {
      throw err;
    }
  }

  // 停止录制并返回 WAV Blob
  async stop(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder || !this.stream) {
        reject(new Error("录制未开始"));
        return;
      }

      this.mediaRecorder.onstop = async () => {
        try {
          // 合并所有音频块
          const audioBlob = new Blob(this.audioChunks, {
            type: this.mediaRecorder?.mimeType,
          });

          // 转换为 WAV (PCM 8kHz)
          const wavBlob = await this.convertToWav(audioBlob);

          // 清理资源
          this.cleanup();

          resolve(wavBlob);
        } catch (err) {
          reject(err);
        }
      };

      this.mediaRecorder.stop();
    });
  }

  // 获取支持的 MIME 类型
  private getSupportedMimeType(): string {
    const types = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/ogg;codecs=opus",
      "audio/ogg",
      "audio/mp4",
      "audio/wav",
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    return "audio/webm";
  }

  // 将录制的音频转换为 WAV 格式 (PCM 8kHz)
  private async convertToWav(audioBlob: Blob): Promise<Blob> {
    // 创建 AudioContext
    this.audioContext = new AudioContext({
      sampleRate: this.options.sampleRate,
    });

    // 读取音频数据
    const arrayBuffer = await audioBlob.arrayBuffer();
    const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);

    // 获取 PCM 数据
    const numberOfChannels = audioBuffer.numberOfChannels;
    const originalSampleRate = audioBuffer.sampleRate;

    // 重采样到目标采样率 (8kHz)
    const targetSampleRate = this.options.targetSampleRate;
    const resampledData = this.resample(audioBuffer, targetSampleRate);

    // 创建 PCM WAV 文件
    const finalBuffer = this.createWavBuffer(
      resampledData,
      numberOfChannels,
      targetSampleRate
    );

    // 关闭 AudioContext
    this.audioContext.close();
    this.audioContext = null;

    return new Blob([finalBuffer], { type: "audio/wav" });
  }

  // 重采样音频数据
  private resample(
    audioBuffer: AudioBuffer,
    targetSampleRate: number
  ): Int16Array {
    const sourceData = audioBuffer.getChannelData(0); // 使用第一个声道
    const sourceSampleRate = audioBuffer.sampleRate;
    const ratio = sourceSampleRate / targetSampleRate;
    const targetLength = Math.floor(sourceData.length / ratio);

    const result = new Int16Array(targetLength);

    for (let i = 0; i < targetLength; i++) {
      const sourceIndex = i * ratio;
      const index = Math.floor(sourceIndex);
      const fraction = sourceIndex - index;

      // 线性插值
      const sample1 = index < sourceData.length ? sourceData[index] : 0;
      const sample2 =
        index + 1 < sourceData.length ? sourceData[index + 1] : sample1;
      const interpolated = sample1 + fraction * (sample2 - sample1);

      // 转换为 16-bit PCM
      result[i] = Math.max(
        -32768,
        Math.min(32767, Math.round(interpolated * 32767))
      );
    }

    return result;
  }

  // 创建 WAV 文件缓冲区 (PCM 16-bit)
  private createWavBuffer(
    pcmData: Int16Array,
    numberOfChannels: number,
    sampleRate: number
  ): ArrayBuffer {
    const dataSize = pcmData.length * 2;
    const buffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buffer);

    // RIFF chunk
    this.writeString(view, 0, "RIFF");
    view.setUint32(4, 36 + dataSize, true);
    this.writeString(view, 8, "WAVE");

    // fmt chunk
    this.writeString(view, 12, "fmt ");
    view.setUint32(16, 16, true); // Subchunk1Size
    view.setUint16(20, 1, true); // AudioFormat (PCM)
    view.setUint16(22, numberOfChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * numberOfChannels * 2, true); // ByteRate
    view.setUint16(32, numberOfChannels * 2, true); // BlockAlign
    view.setUint16(34, 16, true); // BitsPerSample

    // data chunk
    this.writeString(view, 36, "data");
    view.setUint32(40, dataSize, true);

    // 写入 PCM 数据
    for (let i = 0; i < pcmData.length; i++) {
      view.setInt16(44 + i * 2, pcmData[i], true);
    }

    return buffer;
  }

  // 写入字符串到 DataView
  private writeString(view: DataView, offset: number, string: string): void {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  }

  // 清理资源
  private cleanup(): void {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
    this.mediaRecorder = null;
    this.audioChunks = [];
  }

  // 停止并清理
  abort(): void {
    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      this.mediaRecorder.stop();
    }
    this.cleanup();
  }
}

// Hook 返回类型
export interface UseAudioRecorderReturn {
  isRecording: boolean;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<Blob | null>;
  error: string | null;
}
